import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Box,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUpload from "../../GovernanceDashboard/DataEntry/FileUpload";
import { useNavigate, useParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import styles from "../../../styles/social/social.module.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { get, put } from "../../../utils/api";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import SocialLayout from "../../../Layout/SocialLayout";
import WestIcon from "@mui/icons-material/West";
import { textError } from "../../../utils/validationMessage";
import {
  annualPolicyYesFieldErrors,
  getEffectiveDateMinMax,
  hasUploadedDocument,
  isAnnualPolicyUploadDisabled,
  shouldShowAnnualPolicyRevised,
} from "../../../utils/annualPolicyFormHelpers";
import DeleteModal from "../../../Components/Common/DeleteModal";

const SOCIAL_PRIMARY = "#1C486B";
const socialRadioSx = {
  color: SOCIAL_PRIMARY,
  "&.Mui-checked": { color: SOCIAL_PRIMARY },
};
const backButtonSx = {
  color: SOCIAL_PRIMARY,
  textTransform: "none",
  border: `1px solid ${SOCIAL_PRIMARY}`,
  "&:hover": { borderColor: SOCIAL_PRIMARY },
};
const primaryButtonSx = {
  textTransform: "none",
  backgroundColor: SOCIAL_PRIMARY,
  "&:hover": { backgroundColor: "#163a56" },
};

const BooleanInputField = ({ label, name, value, onChange }) => (
  <FormControl component="fieldset" style={{ marginBottom: "1rem" }}>
    <p
      className={styles.form_heading}
      style={{ fontWeight: "bold", marginTop: "1rem" }}
    >
      {label}
    </p>
    <RadioGroup
      row
      name={name}
      value={value === false || value === "false" ? "false" : "true"}
      onChange={onChange}
      sx={{ pl: 2 }}
    >
      <FormControlLabel
        value="true"
        control={<Radio sx={socialRadioSx} />}
        label="Yes"
      />
      <FormControlLabel
        value="false"
        control={<Radio sx={socialRadioSx} />}
        label="No"
      />
    </RadioGroup>
  </FormControl>
);

const EmployeeBenifit = () => {
  const removeCommas = (val) => {
    if (val === null || val === undefined) return "";
    return val.toString().replace(/,/g, "").trim();
  };

  const formatWithCommas = (val) => {
    const normalized = removeCommas(val);
    if (normalized === "") return "";
    if (!/^\d*\.?\d*$/.test(normalized)) return normalized;
    const [integerPart = "", decimalPart] = normalized.split(".");
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimalPart !== undefined
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;
  };

  const navigate = useNavigate();
  const { id } = useParams();
  const [errors, setErrors] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteData, setDeleteData] = useState({ fieldName: "", index: "" });
  const [entryMeta, setEntryMeta] = useState({
    companyId: null,
    year: null,
    month: null,
  });
  const [isFirstEntry, setIsFirstEntry] = useState(false);
  const [previousYearBenefitsDocumentUrl, setPreviousYearBenefitsDocumentUrl] =
    useState("");
  const [entryYear, setEntryYear] = useState(null);
  const todayIso = new Date().toISOString().split("T")[0];
  const currentYear = new Date().getFullYear();
  const maxEffectiveDate =
    Number(entryYear) && Number(entryYear) < currentYear
      ? `${entryYear}-12-31`
      : todayIso;
  const { min: benefitsEffectiveMin, max: benefitsEffectiveMax } =
    getEffectiveDateMinMax(entryYear);

  const defaultBenefitsPrograms = [
    {
      benefit: "Health insurance",
      permanentMale: "",
      permanentFemale: "",
      benefitsInINRPermanent: "",
      otherMale: "",
      otherFemale: "",
      benefitsInINROther: "",
    },
    {
      benefit: "Accident insurance",
      permanentMale: "",
      permanentFemale: "",
      benefitsInINRPermanent: "",
      otherMale: "",
      otherFemale: "",
      benefitsInINROther: "",
    },
    {
      benefit: "Maternity benefits",
      permanentMale: "",
      permanentFemale: "",
      benefitsInINRPermanent: "",
      otherMale: "",
      otherFemale: "",
      benefitsInINROther: "",
    },
    {
      benefit: "Paternity Benefits",
      permanentMale: "",
      permanentFemale: "",
      benefitsInINRPermanent: "",
      otherMale: "",
      otherFemale: "",
      benefitsInINROther: "",
    },
    {
      benefit: "Day Care facilities",
      permanentMale: "",
      permanentFemale: "",
      benefitsInINRPermanent: "",
      otherMale: "",
      otherFemale: "",
      benefitsInINROther: "",
    },
    {
      benefit: "PF (Provident Fund)",
      permanentMale: "",
      permanentFemale: "",
      benefitsInINRPermanent: "",
      otherMale: "",
      otherFemale: "",
      benefitsInINROther: "",
    },
    {
      benefit: "Gratuity",
      permanentMale: "",
      permanentFemale: "",
      benefitsInINRPermanent: "",
      otherMale: "",
      otherFemale: "",
      benefitsInINROther: "",
    },
    {
      benefit: "ESI (Employees' State Insurance)",
      permanentMale: "",
      permanentFemale: "",
      benefitsInINRPermanent: "",
      otherMale: "",
      otherFemale: "",
      benefitsInINROther: "",
    },
    {
      benefit: "Life insurance",
      permanentMale: "",
      permanentFemale: "",
      benefitsInINRPermanent: "",
      otherMale: "",
      otherFemale: "",
      benefitsInINROther: "",
    },
    {
      benefit: "Compensatory package in the event of death",
      permanentMale: "",
      permanentFemale: "",
      benefitsInINRPermanent: "",
      otherMale: "",
      otherFemale: "",
      benefitsInINROther: "",
    },
  ];

  const predefinedBenefitsSet = new Set(
    defaultBenefitsPrograms.map((b) => String(b.benefit || "").trim()),
  );
  const [existingBenefitsCoveredDocUrl, setExistingBenefitsCoveredDocUrl] =
    useState("");
  const [prevBenefitsCoveredInHandbook, setPrevBenefitsCoveredInHandbook] =
    useState({
      documentUrl: "",
      remark: "",
      effectiveDate: "",
    });

  const [formData, setFormData] = useState({
    benefitsCoveredInHandbook: {
      status: true,
      isRevised: false,
      statusremark: "",
      remark: "",
      documentUrl: "",
      effectiveDate: "",
    },
    // Single flat list of rows shown in one table
    benefitsProgramsOffered: defaultBenefitsPrograms,
    // Kept for backward compatibility but no longer rendered
    additionalTables: [],
  });

  const fetchData = async (id) => {
    try {
      const response = await get(`/social/getSocial?id=${id}`);
      const socialData = response?.data?.data?.[0] || {};
      let data = socialData?.employeeBenefits;
      console.log("first41", data);
      const sanitizeDocumentUrl = (value) => {
        if (typeof value !== "string") return value || "";
        const cleaned = value.trim();
        if (!cleaned) return "";
        if (cleaned.toLowerCase() === "no documents available") return "";
        return cleaned;
      };
      const toBoolean = (val, defaultValue = false) => {
        if (typeof val === "boolean") return val;
        if (typeof val === "string") {
          const normalized = val.trim().toLowerCase();
          if (normalized === "true") return true;
          if (normalized === "false") return false;
        }
        return defaultValue;
      };
      const currentEntryYear =
        Number(socialData?._socialDataRef?.yearToSubmit) ||
        Number(socialData?.yearToSubmit) ||
        Number(socialData?.year) ||
        undefined;
      setEntryYear(currentEntryYear || null);
      const companyId = socialData?.company;
      let prevBenefitsPolicy = {};
      try {
        const qs = new URLSearchParams();
        if (currentEntryYear) qs.set("year", String(currentEntryYear));
        if (companyId) qs.set("companyId", String(companyId));
        const prevUrl = `/social/totalWorkforce/annualRangeWithPrev${qs.toString() ? `?${qs.toString()}` : ""}`;
        const prevRes = await get(prevUrl);
        setIsFirstEntry(Boolean(prevRes?.data?.data?.isFirstEntry));
        const prevPolicies = prevRes?.data?.data?.policies || {};
        prevBenefitsPolicy =
          prevPolicies?.["employeeBenefits.benefitsCoveredInHandbook"] || {};
        setPreviousYearBenefitsDocumentUrl(
          sanitizeDocumentUrl(prevBenefitsPolicy?.documentUrl || ""),
        );
      } catch (e) {
        prevBenefitsPolicy = {};
        setIsFirstEntry(false);
        setPreviousYearBenefitsDocumentUrl("");
      }

      setEntryMeta({
        companyId: socialData?.company || null,
        year:
          Number(socialData?._socialDataRef?.yearToSubmit) ||
          Number(socialData?.yearToSubmit) ||
          Number(socialData?.year) ||
          null,
        month:
          Number(socialData?._socialDataRef?.monthToSubmit) ||
          Number(socialData?.monthToSubmit) ||
          Number(socialData?.month) ||
          null,
      });

      // Handle benefitsProgramsOffered from backend as a single flat array of row-objects
      const backendBenefits = Array.isArray(data?.benefitsProgramsOffered)
        ? data.benefitsProgramsOffered
            .filter((row) => row && typeof row === "object")
            .map((row) => {
              const benefitName = String(row.benefit || "").trim();
              const isPredefined = predefinedBenefitsSet.has(benefitName);
              return {
                ...row,
                permanentMale: formatWithCommas(row.permanentMale),
                permanentFemale: formatWithCommas(row.permanentFemale),
                benefitsInINRPermanent: formatWithCommas(
                  row.benefitsInINRPermanent,
                ),
                otherMale: formatWithCommas(row.otherMale),
                otherFemale: formatWithCommas(row.otherFemale),
                benefitsInINROther: formatWithCommas(row.benefitsInINROther),
                // Mark non-predefined rows as custom so UI shows editable field + delete icon
                isCustom: isPredefined ? !!row.isCustom : true,
              };
            })
        : [];

      setFormData((prevData) => ({
        ...prevData,
        benefitsCoveredInHandbook: {
          ...prevData.benefitsCoveredInHandbook,
          ...(data?.benefitsCoveredInHandbook || {}),
          // default Yes only when status is missing; preserve fetched false as No
          status:
            data?.benefitsCoveredInHandbook?.status === undefined
              ? prevBenefitsPolicy?.status === undefined
                ? (prevData.benefitsCoveredInHandbook.status ?? true)
                : toBoolean( true)
              : data?.benefitsCoveredInHandbook?.status === "true"
                ? true
                : data?.benefitsCoveredInHandbook?.status === "false"
                  ? false
                  : data?.benefitsCoveredInHandbook?.status,
          isRevised:
            data?.benefitsCoveredInHandbook?.isRevised === undefined
              ? prevBenefitsPolicy?.isRevised === undefined
                ? (prevData.benefitsCoveredInHandbook.isRevised ?? false)
                : toBoolean(prevBenefitsPolicy?.isRevised, false)
              : data?.benefitsCoveredInHandbook?.isRevised === "true"
                ? true
                : data?.benefitsCoveredInHandbook?.isRevised === "false"
                  ? false
                  : data?.benefitsCoveredInHandbook?.isRevised,
          documentUrl:
            sanitizeDocumentUrl(data?.benefitsCoveredInHandbook?.documentUrl || "") ||
            sanitizeDocumentUrl(prevBenefitsPolicy?.documentUrl || "") ||
            sanitizeDocumentUrl(prevData.benefitsCoveredInHandbook.documentUrl || "") ||
            "",
          remark:
            data?.benefitsCoveredInHandbook?.remark ||
            prevBenefitsPolicy?.remark ||
            prevData.benefitsCoveredInHandbook.remark ||
            "",
          statusremark:
            data?.benefitsCoveredInHandbook?.statusremark ||
            // prevBenefitsPolicy?.statusremark ||
            prevData.benefitsCoveredInHandbook.statusremark ||
            "",
          effectiveDate:
            data?.benefitsCoveredInHandbook?.effectiveDate ||
            prevBenefitsPolicy?.effectiveDate ||
            prevData.benefitsCoveredInHandbook.effectiveDate ||
            "",
        },
        // Use all backend rows in a single table; fall back to defaults
        benefitsProgramsOffered:
          backendBenefits.length > 0
            ? backendBenefits
            : prevData.benefitsProgramsOffered,
        additionalTables: [],
      }));
      const initialPrevBenefitsCoveredDocUrl =
        sanitizeDocumentUrl(data?.benefitsCoveredInHandbook?.documentUrl || "") ||
        sanitizeDocumentUrl(prevBenefitsPolicy?.documentUrl || "") ||
        "";
      setExistingBenefitsCoveredDocUrl(initialPrevBenefitsCoveredDocUrl);
      setPrevBenefitsCoveredInHandbook({
        documentUrl: initialPrevBenefitsCoveredDocUrl,
        remark:
          data?.benefitsCoveredInHandbook?.remark ||
          prevBenefitsPolicy?.remark ||
          "",
        effectiveDate:
          data?.benefitsCoveredInHandbook?.effectiveDate ||
          prevBenefitsPolicy?.effectiveDate ||
          "",
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const validation = () => {
    let newErrors = {}; // Initialize an empty error object
    let hasErrors = false;

    const isEmpty = (value) =>
      value === "" || value === null || value === undefined;

    const validateRecursive = (data, errorsObj) => {
      Object.entries(data).forEach(([key, value]) => {
        // Skip additionalTables - it's validated separately
        if (key === "additionalTables") {
          return;
        }
        if (Array.isArray(value)) {
          // Special handling for benefitsProgramsOffered so we can skip
          // disabled gender fields based on benefit type (maternity/paternity)
          if (key === "benefitsProgramsOffered") {
            const arrayErrors = [];
            value.forEach((item, index) => {
              if (typeof item === "object" && item !== null) {
                const itemErrors = {};
                const isMaternity = item.benefit === "Maternity benefits";
                const isPaternity = item.benefit === "Paternity Benefits";

                // For numeric fields, respect disabled ones:
                // - Maternity: male fields are disabled (permanentMale, otherMale)
                // - Paternity: female fields are disabled (permanentFemale, otherFemale)

                if (!isEmpty(item.permanentMale) || !isMaternity) {
                  if (isEmpty(item.permanentMale) && !isMaternity) {
                    itemErrors.permanentMale = textError;
                  }
                }

                if (!isEmpty(item.permanentFemale) || !isPaternity) {
                  if (isEmpty(item.permanentFemale) && !isPaternity) {
                    itemErrors.permanentFemale = textError;
                  }
                }

                if (isEmpty(item.benefitsInINRPermanent)) {
                  itemErrors.benefitsInINRPermanent = textError;
                }

                if (!isEmpty(item.otherMale) || !isMaternity) {
                  if (isEmpty(item.otherMale) && !isMaternity) {
                    itemErrors.otherMale = textError;
                  }
                }

                if (!isEmpty(item.otherFemale) || !isPaternity) {
                  if (isEmpty(item.otherFemale) && !isPaternity) {
                    itemErrors.otherFemale = textError;
                  }
                }

                if (isEmpty(item.benefitsInINROther)) {
                  itemErrors.benefitsInINROther = textError;
                }

                if (Object.keys(itemErrors).length > 0) {
                  arrayErrors[index] = itemErrors;
                  hasErrors = true;
                }
              }
            });

            if (arrayErrors.length > 0) {
              errorsObj[key] = arrayErrors;
            }
          } else {
            let arrayErrors = [];
            value.forEach((item, index) => {
              if (typeof item === "object" && item !== null) {
                let nestedErrors = {};
                validateRecursive(item, nestedErrors);

                // ✅ If nested errors exist, store them at the correct index
                if (Object.keys(nestedErrors).length > 0) {
                  arrayErrors[index] = nestedErrors;
                }
              } else if (isEmpty(item)) {
                arrayErrors[index] = textError; // ✅ Store error at the correct index
              }
            });

            if (arrayErrors.length > 0) {
              errorsObj[key] = arrayErrors;
              hasErrors = true;
            }
          }
        } else if (typeof value === "object" && value !== null) {
          let nestedErrors = {};

          // ✅ Validate `documentUrl` only if `status: true`, else remove the key
          if (["benefitsCoveredInHandbook"].includes(key)) {
            if (value.status) {
              const showRev = shouldShowAnnualPolicyRevised(
                !isFirstEntry,
                previousYearBenefitsDocumentUrl,
              );
              const polErr = annualPolicyYesFieldErrors(
                value,
                entryYear,
                showRev,
              );
              Object.assign(nestedErrors, polErr);
              if (Object.keys(polErr).length) hasErrors = true;
            }
          } else {
            validateRecursive(value, nestedErrors);
          }

          if (Object.keys(nestedErrors).length > 0) {
            errorsObj[key] = nestedErrors;
            hasErrors = true;
          }
        } else if (isEmpty(value)) {
          errorsObj[key] = textError;
          hasErrors = true;
        }
      });
    };

    validateRecursive(formData, newErrors);

    // Validate additional tables
    if (formData.additionalTables && Array.isArray(formData.additionalTables)) {
      // Additional tables are no longer rendered; skip their validation
    }

    if (formData.benefitsCoveredInHandbook?.status === false) {
      delete newErrors.benefitsCoveredInHandbook;
    }
    setErrors(newErrors); // ✅ Directly sets nested errors in state
    return hasErrors;
  };

  useEffect(() => {
    fetchData(id);
  }, [id]);

  useEffect(() => {
    const { companyId, year } = entryMeta || {};
    if (!companyId || !year) return;

    const fetchPending = async () => {
      try {
        const qs = new URLSearchParams();
        qs.set("year", String(year));
        qs.set("companyId", String(companyId));
        const prevUrl = `/social/totalWorkforce/annualRangeWithPrev${qs.toString() ? `?${qs.toString()}` : ""}`;
        await get(prevUrl);
      } catch (error) {
        console.error("Failed to fetch annualRangeWithPrev", error);
      }
    };

    fetchPending();
  }, [entryMeta]);

  const handleAddNewTable = () => {
    setFormData((prev) => ({
      ...prev,
      additionalTables: [
        ...prev.additionalTables,
        defaultBenefitsPrograms.map((item) => ({ ...item })), // Deep copy of default benefits
      ],
    }));
  };

  // Add a new empty row to the main Employee Benefits table (not a new table)
  const handleAddBenefitsRow = () => {
    setFormData((prev) => ({
      ...prev,
      benefitsProgramsOffered: [
        ...prev.benefitsProgramsOffered,
        {
          isCustom: true, // mark this as a user-added row
          benefit: "",
          permanentMale: "",
          permanentFemale: "",
          benefitsInINRPermanent: "",
          otherMale: "",
          otherFemale: "",
          benefitsInINROther: "",
        },
      ],
    }));
  };

  const handleDeleteBenefitsRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      benefitsProgramsOffered: prev.benefitsProgramsOffered.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  const handleDelete = () => {
    if (deleteData.fieldName === "benefitsProgramsOffered") {
      handleDeleteBenefitsRow(Number(deleteData.index));
    }
    setDeleteDialog(false);
    setDeleteData({ fieldName: "", index: "" });
  };

  const handleBenefitsTableChange = (
    index,
    field,
    value,
    tableIndex = null,
  ) => {
    const headcountFields = [
      "permanentMale",
      "permanentFemale",
      "otherMale",
      "otherFemale",
    ];
    const inrFields = ["benefitsInINRPermanent", "benefitsInINROther"];
    const normalizedValue = removeCommas(value);
    const isNumericField = headcountFields.includes(field) || inrFields.includes(field);

    // Block words/negative values for male, female and INR fields
    if (headcountFields.includes(field) && !/^\d*$/.test(normalizedValue)) return;
    if (inrFields.includes(field) && !/^\d*\.?\d*$/.test(normalizedValue)) return;
    const finalValue = isNumericField ? formatWithCommas(normalizedValue) : value;

    setFormData((prev) => {
      // If tableIndex is provided, update additional table, otherwise update main table
      if (tableIndex !== null && tableIndex >= 0) {
        const updatedTables = [...prev.additionalTables];
        const updatedBenefits = [...updatedTables[tableIndex]];
        const currentRow = updatedBenefits[index];
        const isMaternity = currentRow.benefit === "Maternity benefits";
        const isPaternity = currentRow.benefit === "Paternity Benefits";

        // Block edits to disabled fields:
        if (
          (isMaternity &&
            (field === "permanentMale" || field === "otherMale")) ||
          (isPaternity &&
            (field === "permanentFemale" || field === "otherFemale"))
        ) {
          return prev;
        }

        updatedBenefits[index] = {
          ...currentRow,
          [field]: finalValue, // store all fields as text so user can enter freely
        };
        updatedTables[tableIndex] = updatedBenefits;
        return {
          ...prev,
          additionalTables: updatedTables,
        };
      } else {
        const updatedBenefits = [...prev.benefitsProgramsOffered];
        const currentRow = updatedBenefits[index];
        const isMaternity = currentRow.benefit === "Maternity benefits";
        const isPaternity = currentRow.benefit === "Paternity Benefits";

        // Block edits to disabled fields:
        if (
          (isMaternity &&
            (field === "permanentMale" || field === "otherMale")) ||
          (isPaternity &&
            (field === "permanentFemale" || field === "otherFemale"))
        ) {
          return prev;
        }

        updatedBenefits[index] = {
          ...currentRow,
          [field]: finalValue, // store all fields as text so user can enter freely
        };
        return {
          ...prev,
          benefitsProgramsOffered: updatedBenefits,
        };
      }
    });

    // Clear errors for this field
    setErrors((prev) => {
      const updatedErrors = { ...prev };
      if (tableIndex !== null && tableIndex >= 0) {
        // Handle errors for additional tables
        const tableKey = `additionalTables_${tableIndex}`;
        if (updatedErrors[tableKey]?.[index]) {
          const updatedFieldErrors = { ...updatedErrors[tableKey][index] };
          delete updatedFieldErrors[field];
          if (Object.keys(updatedFieldErrors).length === 0) {
            const updatedArrayErrors = [...(updatedErrors[tableKey] || [])];
            delete updatedArrayErrors[index];
            const hasAnyError = updatedArrayErrors.some(
              (errorItem) => errorItem && Object.keys(errorItem).length > 0,
            );
            if (hasAnyError) {
              updatedErrors[tableKey] = updatedArrayErrors;
            } else {
              delete updatedErrors[tableKey];
            }
          } else {
            updatedErrors[tableKey] = [...(updatedErrors[tableKey] || [])];
            updatedErrors[tableKey][index] = updatedFieldErrors;
          }
        }
      } else {
        if (updatedErrors.benefitsProgramsOffered?.[index]) {
          const updatedFieldErrors = {
            ...updatedErrors.benefitsProgramsOffered[index],
          };
          delete updatedFieldErrors[field];
          if (Object.keys(updatedFieldErrors).length === 0) {
            const updatedArrayErrors = [
              ...(updatedErrors.benefitsProgramsOffered || []),
            ];
            delete updatedArrayErrors[index];
            const hasAnyError = updatedArrayErrors.some(
              (errorItem) => errorItem && Object.keys(errorItem).length > 0,
            );
            if (hasAnyError) {
              updatedErrors.benefitsProgramsOffered = updatedArrayErrors;
            } else {
              delete updatedErrors.benefitsProgramsOffered;
            }
          } else {
            updatedErrors.benefitsProgramsOffered = [
              ...(updatedErrors.benefitsProgramsOffered || []),
            ];
            updatedErrors.benefitsProgramsOffered[index] = updatedFieldErrors;
          }
        }
      }
      return updatedErrors;
    });
  };

  console.log("first4", formData);

  const handleNestedBooleanChange = (e) => {
    const { name, value } = e.target;
    const [parent, key] = name.split(".");
    // setFormData((prev) => ({
    //   ...prev,
    //   [parent]: {
    //     ...prev[parent],
    //     [key]: value === "true",
    //   },
    // }));
    setFormData((prev) => {
      let updatedParent = {
        ...prev[parent], // Spread existing nested object
        [key]: value === "true",
      };
      console.log(updatedParent, "updatedParent");
      if (
        parent === "benefitsCoveredInHandbook" &&
        key === "isRevised" &&
        updatedParent.isRevised === false
      ) {
        updatedParent.documentUrl =
          existingBenefitsCoveredDocUrl ||
          prevBenefitsCoveredInHandbook.documentUrl ||
          updatedParent.documentUrl ||
          "";
        updatedParent.remark =
          prevBenefitsCoveredInHandbook.remark || updatedParent.remark || "";
        updatedParent.effectiveDate =
          prevBenefitsCoveredInHandbook.effectiveDate ||
          updatedParent.effectiveDate ||
          "";
      }
      if (
        parent === "benefitsCoveredInHandbook" &&
        key === "isRevised" &&
        updatedParent.isRevised === true
      ) {
        updatedParent.documentUrl = "";
        updatedParent.remark = "";
        updatedParent.effectiveDate = "";
      }
      if ("documentUrl" in updatedParent && updatedParent.status === false) {
        updatedParent.documentUrl = "";
        if ("remark" in updatedParent) updatedParent.remark = "";
        if ("effectiveDate" in updatedParent) updatedParent.effectiveDate = "";
        if (parent === "benefitsCoveredInHandbook") {
          updatedParent.isRevised = false;
        }
      }

      return {
        ...prev,
        [parent]: updatedParent,
      };
    });
    setErrors((prev) => {
      const next = { ...prev };
      if (parent === "benefitsCoveredInHandbook") delete next[parent];
      else next[parent] = "";
      return next;
    });
  };

  const handleNestedFieldChange = (e) => {
    const { name, value } = e.target;
    const [parent, key] = name.split(".");
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [key]: value,
      },
    }));
    setErrors((prev) => {
      const next = { ...prev };
      if (parent === "benefitsCoveredInHandbook") {
        const cur =
          typeof next.benefitsCoveredInHandbook === "object" &&
          next.benefitsCoveredInHandbook
            ? { ...next.benefitsCoveredInHandbook }
            : {};
        if (key === "remark" && String(value || "").trim()) delete cur.remark;
        if (key === "effectiveDate") delete cur.effectiveDate;
        if (Object.keys(cur).length) next.benefitsCoveredInHandbook = cur;
        else delete next.benefitsCoveredInHandbook;
      } else {
        next[parent] = "";
      }
      return next;
    });
  };

  const handleFileChange = (file, policy) => {
    setFormData((prev) => ({
      ...prev,
      [policy]: {
        ...prev[policy],
        documentUrl: file,
      },
    }));
    if (policy === "benefitsCoveredInHandbook" && hasUploadedDocument(file)) {
      setErrors((prev) => {
        const next = { ...prev };
        const cur =
          typeof next.benefitsCoveredInHandbook === "object" &&
          next.benefitsCoveredInHandbook
            ? { ...next.benefitsCoveredInHandbook }
            : {};
        delete cur.documentUrl;
        if (Object.keys(cur).length) next.benefitsCoveredInHandbook = cur;
        else delete next.benefitsCoveredInHandbook;
        return next;
      });
    } else if (policy !== "benefitsCoveredInHandbook") {
      setErrors((prev) => ({
        ...prev,
        [policy]: "",
      }));
    }
  };

  const handleSubmit = async () => {
    console.log(formData, "formData");
    if (validation()) return;

    // Send a single flat benefitsProgramsOffered array (main table only)
    const payload = {
      ...formData,
      additionalTables: [],
      benefitsProgramsOffered: (formData.benefitsProgramsOffered || []).map(
        (row) => ({
          ...row,
          permanentMale: removeCommas(row?.permanentMale),
          permanentFemale: removeCommas(row?.permanentFemale),
          benefitsInINRPermanent: removeCommas(row?.benefitsInINRPermanent),
          otherMale: removeCommas(row?.otherMale),
          otherFemale: removeCommas(row?.otherFemale),
          benefitsInINROther: removeCommas(row?.benefitsInINROther),
        }),
      ),
      benefitsCoveredInHandbook: {
        ...formData.benefitsCoveredInHandbook,
        ...(formData.benefitsCoveredInHandbook.status === false
          ? { documentUrl: "", isRevised: false, remark: "", effectiveDate: "" }
          : {}),
      },
    };

    await put(`/social/updateSocial?Id=${id}`, {
      employeeBenefits: payload,
      stepsCompleted: {
        employeeBenefits: true,
      },
    })
      .then((res) => {
        navigate(`/social/data/customer-satisfaction/${id}`);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const displayedBenefitsPolicyDocument =
    formData?.benefitsCoveredInHandbook?.documentUrl ||
    (formData?.benefitsCoveredInHandbook?.isRevised
      ? ""
      : existingBenefitsCoveredDocUrl ||
        prevBenefitsCoveredInHandbook.documentUrl ||
        "");

  return (
    <MainDashboardLayout sidebar={<SocialLayout />}>
      <DeleteModal
        onOpen={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
      />
      {/* <div style={{ padding: "1rem 0" }}>
        <span
          style={{
            fontSize: "14px",
            fontWeight: "400",
            color: "rgba(0, 0, 0, 0.8)",
          }}
        >
          Data{" "}
        </span>
        <span>
          <ChevronRightIcon />
        </span>{" "}
        <span
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "rgba(0, 0, 0, 1)",
          }}
        >
          Add New Entry
        </span>
      </div>
      <div
        style={{
          fontSize: "24px",
          fontWeight: "700",
          color: "#001632",
          marginBottom: "1rem",
        }}
      >
        Add New Entry
      </div> */}
      <p className={styles.form_headingStrongUpper}>G. EMPLOYEE BENIFITS</p>

      <div style={{ width: "100%", marginBottom: "1rem", marginTop: "1rem" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <p
            className={styles.form_heading}
            style={{ fontWeight: "bold", margin: 0 }}
          >
            1. Employee Benefits Programs Offered:
          </p>
        </div>

        {/* Render Main Table (single table for all rows) */}
        <div>
          <TableContainer
            component={Paper}
            sx={{ marginTop: "10px", boxShadow: "none" }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      backgroundColor: "#1C486B",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      padding: "8px 16px",
                      borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                      verticalAlign: "middle",
                    }}
                  >
                    Employee Benefits Programs Offered
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    sx={{
                      backgroundColor: "#1C486B",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      padding: "8px 16px",
                      borderRight: "2px solid rgba(255, 255, 255, 0.5)",
                      textAlign: "center",
                    }}
                  >
                    Number of Employees benefited (Permanent Employees)
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      backgroundColor: "#1C486B",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      padding: "8px 16px",
                      borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                      verticalAlign: "middle",
                    }}
                  >
                    Benefits in INR
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    sx={{
                      backgroundColor: "#1C486B",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      padding: "8px 16px",
                      borderRight: "2px solid rgba(255, 255, 255, 0.5)",
                      textAlign: "center",
                    }}
                  >
                    Number of Employees benefited (Other than Permanent
                    Employees)
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      backgroundColor: "#1C486B",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      padding: "8px 16px",
                      borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                      verticalAlign: "middle",
                    }}
                  >
                    Benefits in INR
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "#1C486B",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      padding: "8px 16px",
                      borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    Male
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#1C486B",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      padding: "8px 16px",
                      borderRight: "2px solid rgba(255, 255, 255, 0.5)",
                    }}
                  >
                    Female
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#1C486B",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      padding: "8px 16px",
                      borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    Male
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#1C486B",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      padding: "8px 16px",
                      borderRight: "2px solid rgba(255, 255, 255, 0.5)",
                    }}
                  >
                    Female
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.benefitsProgramsOffered.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        // padding: "8px 16px",
                        borderRight: "1px solid #E0E0E0",
                        fontWeight: "500",
                        padding:item.isCustom? "8.5px 14px 8.5px 0px !important":"8.5px 14px 8.5px 14px",
                      }}
                    >
                      {item.isCustom ? (
                        <TextField
                          value={item.benefit || ""}
                          onChange={(e) =>
                            handleBenefitsTableChange(
                              index,
                              "benefit",
                              e.target.value,
                            )
                          }
                          variant="outlined"
                          multiline
                          minRows={1}
                          size="small"
                          fullWidth
                          placeholder="Employee Benefits Programs Offered"
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                            
                          }}
                        />
                      ) : (
                        item.benefit
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "8px 16px",
                        borderRight: "1px solid #E0E0E0",
                      }}
                    >
                      <TextField
                        value={
                          item.benefit === "Maternity benefits"
                            ? "-"
                            : item.permanentMale || ""
                        }
                        onChange={(e) =>
                          handleBenefitsTableChange(
                            index,
                            "permanentMale",
                            e.target.value,
                          )
                        }
                        multiline
                        minRows={1}
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Enter"
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                        }}
                        disabled={item.benefit === "Maternity benefits"}
                        error={
                          item.benefit !== "Maternity benefits" &&
                          !!errors?.benefitsProgramsOffered?.[index]
                            ?.permanentMale
                        }
                      />
                      {item.benefit !== "Maternity benefits" &&
                        errors?.benefitsProgramsOffered?.[index]
                          ?.permanentMale && (
                          <FormHelperText
                            sx={{
                              color: "#D34040",
                              fontSize: "12px",
                              margin: 0,
                              paddingTop: "4px",
                            }}
                          >
                            {
                              errors.benefitsProgramsOffered[index]
                                .permanentMale
                            }
                          </FormHelperText>
                        )}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "8px 16px",
                        borderRight: "2px solid #E0E0E0",
                      }}
                    >
                      <TextField
                        value={
                          item.benefit === "Paternity Benefits"
                            ? "-"
                            : item.permanentFemale || ""
                        }
                        onChange={(e) =>
                          handleBenefitsTableChange(
                            index,
                            "permanentFemale",
                            e.target.value,
                          )
                        }
                        multiline
                        minRows={1}
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Enter"
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                        }}
                        disabled={item.benefit === "Paternity Benefits"}
                        error={
                          item.benefit !== "Paternity Benefits" &&
                          !!errors?.benefitsProgramsOffered?.[index]
                            ?.permanentFemale
                        }
                      />
                      {item.benefit !== "Paternity Benefits" &&
                        errors?.benefitsProgramsOffered?.[index]
                          ?.permanentFemale && (
                          <FormHelperText
                            sx={{
                              color: "#D34040",
                              fontSize: "12px",
                              margin: 0,
                              paddingTop: "4px",
                            }}
                          >
                            {
                              errors.benefitsProgramsOffered[index]
                                .permanentFemale
                            }
                          </FormHelperText>
                        )}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "8px 16px",
                        borderRight: "1px solid #E0E0E0",
                      }}
                    >
                      <TextField
                        value={item.benefitsInINRPermanent || ""}
                        onChange={(e) =>
                          handleBenefitsTableChange(
                            index,
                            "benefitsInINRPermanent",
                            e.target.value,
                          )
                        }
                        multiline
                        minRows={1}
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Enter"
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                        }}
                        error={
                          !!errors?.benefitsProgramsOffered?.[index]
                            ?.benefitsInINRPermanent
                        }
                      />
                      {errors?.benefitsProgramsOffered?.[index]
                        ?.benefitsInINRPermanent && (
                        <FormHelperText
                          sx={{
                            color: "#D34040",
                            fontSize: "12px",
                            margin: 0,
                            paddingTop: "4px",
                          }}
                        >
                          {
                            errors.benefitsProgramsOffered[index]
                              .benefitsInINRPermanent
                          }
                        </FormHelperText>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "8px 16px",
                        borderRight: "1px solid #E0E0E0",
                      }}
                    >
                      <TextField
                        value={
                          item.benefit === "Maternity benefits"
                            ? "-"
                            : item.otherMale || ""
                        }
                        multiline
                        minRows={1}
                        onChange={(e) =>
                          handleBenefitsTableChange(
                            index,
                            "otherMale",
                            e.target.value,
                          )
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Enter"
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                        }}
                        disabled={item.benefit === "Maternity benefits"}
                        error={
                          item.benefit !== "Maternity benefits" &&
                          !!errors?.benefitsProgramsOffered?.[index]?.otherMale
                        }
                      />
                      {item.benefit !== "Maternity benefits" &&
                        errors?.benefitsProgramsOffered?.[index]?.otherMale && (
                          <FormHelperText
                            sx={{
                              color: "#D34040",
                              fontSize: "12px",
                              margin: 0,
                              paddingTop: "4px",
                            }}
                          >
                            {errors.benefitsProgramsOffered[index].otherMale}
                          </FormHelperText>
                        )}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "8px 16px",
                        borderRight: "2px solid #E0E0E0",
                      }}
                    >
                      <TextField
                        value={
                          item.benefit === "Paternity Benefits"
                            ? "-"
                            : item.otherFemale || ""
                        }
                        multiline
                        minRows={1}
                        onChange={(e) =>
                          handleBenefitsTableChange(
                            index,
                            "otherFemale",
                            e.target.value,
                          )
                        }
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Enter"
                        sx={{
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                        }}
                        disabled={item.benefit === "Paternity Benefits"}
                        error={
                          item.benefit !== "Paternity Benefits" &&
                          !!errors?.benefitsProgramsOffered?.[index]
                            ?.otherFemale
                        }
                      />
                      {item.benefit !== "Paternity Benefits" &&
                        errors?.benefitsProgramsOffered?.[index]
                          ?.otherFemale && (
                          <FormHelperText
                            sx={{
                              color: "#D34040",
                              fontSize: "12px",
                              margin: 0,
                              paddingTop: "4px",
                            }}
                          >
                            {errors.benefitsProgramsOffered[index].otherFemale}
                          </FormHelperText>
                        )}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "8px 16px",
                        borderRight: "1px solid #E0E0E0",
                        position: "relative",
                      }}
                    >
                      <TextField
                        value={item.benefitsInINROther || ""}
                        onChange={(e) =>
                          handleBenefitsTableChange(
                            index,
                            "benefitsInINROther",
                            e.target.value,
                          )
                        }
                        multiline
                        minRows={1}
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder="Enter"
                        sx={{
                          pr: item.isCustom ? 4 : 0, // leave room for delete icon on custom rows
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                        }}
                        error={
                          !!errors?.benefitsProgramsOffered?.[index]
                            ?.benefitsInINROther
                        }
                      />
                      {errors?.benefitsProgramsOffered?.[index]
                        ?.benefitsInINROther && (
                        <FormHelperText
                          sx={{
                            color: "#D34040",
                            fontSize: "12px",
                            margin: 0,
                            paddingTop: "4px",
                          }}
                        >
                          {
                            errors.benefitsProgramsOffered[index]
                              .benefitsInINROther
                          }
                        </FormHelperText>
                      )}
                      {item.isCustom && (
                        <DeleteIcon
                          onClick={() => {
                            setDeleteDialog(true);
                            setDeleteData({
                              fieldName: "benefitsProgramsOffered",
                              index,
                            });
                          }}
                          sx={{
                            position: "absolute",
                            right: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "#D34040",
                            fontSize: 20,
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleAddBenefitsRow}
              sx={{
                textTransform: "none",
                minWidth: "auto",
                padding: "6px 12px",
                backgroundColor: "#FFFFFF",
                borderColor: "#1C486B",
                borderWidth: "1px",
                borderStyle: "solid",
                color: "#1C486B",
                "&:hover": {
                  backgroundColor: "#FFFFFF",
                  borderColor: "#1C486B",
                },
              }}
            >
              Add +
            </Button>
          </div>
        </div>
      </div>

      <BooleanInputField
        label="2. Employee Benefit Policy covered in employment handbook:"
        name="benefitsCoveredInHandbook.status"
        value={formData?.benefitsCoveredInHandbook?.status ?? true}
        onChange={handleNestedBooleanChange}
      />

      {formData?.benefitsCoveredInHandbook?.status ? (
        <div
          style={{
            marginTop: "-1.5rem",
            marginBottom: "1rem",
            marginLeft: "0.7rem",
          }}
        >
            {shouldShowAnnualPolicyRevised(
              !isFirstEntry,
              previousYearBenefitsDocumentUrl,
            ) && (
              <BooleanInputField
                label="Is the Policy revised?"
                name="benefitsCoveredInHandbook.isRevised"
                value={formData?.benefitsCoveredInHandbook?.isRevised ?? false}
                onChange={handleNestedBooleanChange}
              />
            )}
          <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
            Upload evidence (for Policy):
          </p>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: hasUploadedDocument(displayedBenefitsPolicyDocument)
                  ? "233px"
                  : "100px",
              }}
            >
              <FileUpload
                setDocumentUrl={(file) =>
                  handleFileChange(file, "benefitsCoveredInHandbook")
                }
                filesList={displayedBenefitsPolicyDocument}
                isDisabled={isAnnualPolicyUploadDisabled(
                  !isFirstEntry,
                  previousYearBenefitsDocumentUrl,
                )}
              />
              {typeof errors?.benefitsCoveredInHandbook === "object" &&
              errors.benefitsCoveredInHandbook?.documentUrl ? (
                <FormHelperText
                  sx={{ color: "#D34040", fontSize: "14px", minHeight: "20px" }}
                >
                  {errors.benefitsCoveredInHandbook.documentUrl}
                </FormHelperText>
              ) : null}
            </Box>
            <Box
              sx={{
                width: "30%",
                mt: hasUploadedDocument(displayedBenefitsPolicyDocument)
                  ? "15px"
                  : "10px",
                  ml:hasUploadedDocument(displayedBenefitsPolicyDocument) ? "0" : "1.1rem",
              }}
            >
              <TextField
                fullWidth
                name="benefitsCoveredInHandbook.remark"
                value={formData?.benefitsCoveredInHandbook?.remark || ""}
                onChange={handleNestedFieldChange}
                placeholder="Enter Additional Text"
                size="small"
                multiline
                minRows={1}
                error={
                  !!(
                    typeof errors?.benefitsCoveredInHandbook === "object" &&
                    errors.benefitsCoveredInHandbook?.remark
                  )
                }
                helperText={
                  typeof errors?.benefitsCoveredInHandbook === "object"
                    ? errors.benefitsCoveredInHandbook?.remark || " "
                    : " "
                }
                InputProps={{ sx: { padding: "9px 12px" } }}
              />
            </Box>
          </Box>
          <p
            className={styles.form_heading}
            style={{ fontWeight: "bold", marginTop: "1rem",ml: "-0.1rem",mb:"12px" }}
          >
            Effective date (for Policy):
          </p>
          <TextField
            type="date"
            name="benefitsCoveredInHandbook.effectiveDate"
            value={formData?.benefitsCoveredInHandbook?.effectiveDate || ""}
            onChange={handleNestedFieldChange}
            inputProps={{
              min: "2021-01-01",
              ...(benefitsEffectiveMax
                ? { max: benefitsEffectiveMax }
                : { max: maxEffectiveDate }),
            }}
            size="small"
            error={
              !!(
                typeof errors?.benefitsCoveredInHandbook === "object" &&
                errors.benefitsCoveredInHandbook?.effectiveDate
              )
            }
            sx={{ width: "30%", ml: "0.1rem", "& .MuiInputBase-root": { height: 40 } }}
          />
          <FormHelperText
            sx={{
              color: "#D34040",
              marginLeft: "0.1rem",
              fontSize: "14px",
              width: "30%",
            }}
          >
            {typeof errors?.benefitsCoveredInHandbook === "object"
              ? errors.benefitsCoveredInHandbook?.effectiveDate
              : ""}
          </FormHelperText>
        </div>
      ) : (
        <div
      style={{
        marginTop: "0.5rem",
        marginBottom: "1rem",
        marginLeft: "1rem",
      }}
    >
      {/* <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
        Remark
      </p> */}
      <TextField
        name="benefitsCoveredInHandbook.statusremark"
        value={formData?.benefitsCoveredInHandbook?.statusremark || ""}
        onChange={handleNestedFieldChange}
        placeholder="Enter Additional Text"
        size="small"
        sx={{ width: "40%" }}
      />
    </div>
      )}

      <Box
        sx={{
          width: "100%",
          mt: "10%",
          mb: 5,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="outlined"
          sx={backButtonSx}
          onClick={() => navigate(`/social/data/human-rights/${id}`)}
        >
          <WestIcon sx={{ mr: 2 }} /> Back
        </Button>
        <Button
          variant="contained"
          className={styles.submit_button}
          onClick={handleSubmit}
          sx={primaryButtonSx}
        >
          Save & Continue <ArrowForwardIcon sx={{ ml: 1 }} />
        </Button>
      </Box>
    </MainDashboardLayout>
  );
};

export default EmployeeBenifit;
